export const generatePastQuarters = () => {
  const quarters = [];
  const currentDate = new Date();
  let month = currentDate.getMonth();
  const year = currentDate.getFullYear();

  for (let i = 0; i < 3; i++) {
    if (month < 0) {
      month += 12;
    }
    const quarter = Math.floor(month / 3);
    const quarterLabel = `Q${quarter + 1} ${year - (quarter === 3 ? 1 : 0)}`;
    quarters.push(quarterLabel);
    month -= 3;
  }
  return quarters;
};

export const generatePastMonths = () => {
  const months = [];
  const currentDate = new Date();
  let month = currentDate.getMonth();
  let year = currentDate.getFullYear();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  for (let i = 0; i < 3; i++) {
    if (month < 0) {
      month += 12;
      year -= 1;
    }
    const monthLabel = `${monthNames[month]} ${year}`;
    months.push(monthLabel);
    month -= 1;
  }
  return months;
};

/**
 * Get the current quarter string (e.g., "Q1 2026")
 */
export const getCurrentQuarter = (): string => {
  const currentDate = new Date();
  const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
  return `Q${quarter} ${currentDate.getFullYear()}`;
};

/**
 * Generate all quarters for a specific year (e.g., 2025)
 */
export const generateQuartersForYear = (year: number): string[] => {
  return [`Q1 ${year}`, `Q2 ${year}`, `Q3 ${year}`, `Q4 ${year}`];
};

/**
 * Get the end date of a quarter.
 * Q1 ends March 31, Q2 ends June 30, Q3 ends Sept 30, Q4 ends Dec 31.
 */
const getQuarterEndDate = (quarter: number, year: number): Date => {
  const endMonth = quarter * 3; // 3, 6, 9, 12
  return new Date(year, endMonth, 0, 23, 59, 59); // last day of end month
};

/**
 * Filter quarters to only include periods eligible for review.
 * Rules:
 * - Past quarters are always visible
 * - The current quarter is always visible
 * - Future quarters are never visible
 */
export const filterQuartersByEligibility = (quarters: string[]): string[] => {
  const now = new Date();

  return quarters.filter((q) => {
    const match = q.match(/Q(\d)\s+(\d{4})/);
    if (!match) return false;
    const qNum = parseInt(match[1], 10);
    const year = parseInt(match[2], 10);
    const endDate = getQuarterEndDate(qNum, year);
    const startOfQuarter = new Date(year, (qNum - 1) * 3, 1);

    // Past quarter (already ended) — always show
    if (endDate < now) return true;

    // Current quarter (we're inside it) — always show
    if (startOfQuarter <= now && now <= endDate) {
      return true;
    }

    // Future quarter — hide
    return false;
  });
};

/**
 * Interface for submissions that have a reviewPeriod and status
 */
interface SubmissionWithPeriod {
  reviewPeriod?: string;
  status?: string;
}

/**
 * Get available quarters for BSC/PEAR submission.
 * Returns quarters for specified years that haven't been submitted yet.
 * A submitted review is one with status 'submitted' or 'approved'.
 * 
 * @param existingSubmissions - Array of existing submissions with reviewPeriod and status
 * @param years - Array of years to generate quarters for (default: [2025, current year])
 * @returns Array of available quarter strings
 */
export const getAvailableQuartersForSubmission = (
  existingSubmissions: SubmissionWithPeriod[] | undefined,
  years?: number[]
): string[] => {
  const currentYear = new Date().getFullYear();
  
  // 2025 and prior years are closed for submissions - only current year allowed
  const yearsToInclude = years || [currentYear];
  
  // Generate all quarters for specified years
  const allQuarters = yearsToInclude.flatMap(year => generateQuartersForYear(year));
  
  // Apply eligibility filter (only past quarters + current quarter if within 1 week of end)
  const eligibleQuarters = filterQuartersByEligibility(allQuarters);
  
  // Get submitted periods (status is 'submitted' or 'approved')
  const submittedPeriods = new Set(
    (existingSubmissions || [])
      .filter(sub => sub.status === 'submitted' || sub.status === 'approved')
      .map(sub => sub.reviewPeriod)
      .filter(Boolean)
  );
  
  // Filter out submitted quarters
  const availableQuarters = eligibleQuarters.filter(q => !submittedPeriods.has(q));
  
  // Sort to maintain quarter order (year first, then quarter)
  availableQuarters.sort((a, b) => {
    const matchA = a.match(/Q(\d) (\d+)/);
    const matchB = b.match(/Q(\d) (\d+)/);
    if (!matchA || !matchB) return 0;
    const [, qA, yA] = matchA;
    const [, qB, yB] = matchB;
    return Number(yA) - Number(yB) || Number(qA) - Number(qB);
  });
  
  return availableQuarters;
};

/**
 * Get the default quarter to select.
 * Returns current quarter if available, or the first available unsubmitted quarter.
 * 
 * @param availableQuarters - Array of available quarter strings
 * @returns The quarter string to default select, or empty string if none
 */
export const getDefaultQuarterSelection = (availableQuarters: string[]): string => {
  if (!availableQuarters || availableQuarters.length === 0) return '';
  
  const currentQuarter = getCurrentQuarter();
  
  // If current quarter is available, select it
  if (availableQuarters.includes(currentQuarter)) {
    return currentQuarter;
  }
  
  // Otherwise return the first available quarter
  return availableQuarters[0] || '';
};

// ============================================================
// MONTHLY PERIOD UTILITIES (for SPEAR, Life Wheel)
// ============================================================

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

/**
 * Get the current month string (e.g., "February 2026")
 */
export const getCurrentMonth = (): string => {
  const currentDate = new Date();
  return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
};

/**
 * Generate all months for a specific year
 */
export const generateMonthsForYear = (year: number): string[] => {
  return MONTH_NAMES.map(month => `${month} ${year}`);
};

/**
 * Get available months for submission.
 * Returns months for specified years that haven't been submitted yet.
 * A submitted review is one with status 'submitted' or 'approved' (or no status for simpler models).
 * 
 * @param existingSubmissions - Array of existing submissions with reviewPeriod and optional status
 * @param years - Array of years to generate months for (default: [2025, current year])
 * @returns Array of available month strings
 */
export const getAvailableMonthsForSubmission = (
  existingSubmissions: SubmissionWithPeriod[] | undefined,
  years?: number[]
): string[] => {
  const currentYear = new Date().getFullYear();
  const currentMonth = getCurrentMonth();
  
  // Default to 2025 and current year (if different)
  const yearsToInclude = years || (currentYear === 2025 ? [2025] : [2025, currentYear]);
  
  // Generate all months for specified years
  const allMonths = yearsToInclude.flatMap(year => generateMonthsForYear(year));
  
  // Get submitted periods - for simpler models, any existing submission counts
  const submittedPeriods = new Set(
    (existingSubmissions || [])
      .filter(sub => !sub.status || sub.status === 'submitted' || sub.status === 'approved')
      .map(sub => sub.reviewPeriod)
      .filter(Boolean)
  );
  
  // Filter out submitted months
  const availableMonths = allMonths.filter(m => !submittedPeriods.has(m));
  
  // Add current month if it's not already included
  if (!availableMonths.includes(currentMonth) && !submittedPeriods.has(currentMonth)) {
    availableMonths.push(currentMonth);
  }
  
  // Sort by date (year first, then month)
  availableMonths.sort((a, b) => {
    const matchA = a.match(/(\w+) (\d+)/);
    const matchB = b.match(/(\w+) (\d+)/);
    if (!matchA || !matchB) return 0;
    const [, monthA, yA] = matchA;
    const [, monthB, yB] = matchB;
    const monthIndexA = MONTH_NAMES.indexOf(monthA);
    const monthIndexB = MONTH_NAMES.indexOf(monthB);
    return Number(yA) - Number(yB) || monthIndexA - monthIndexB;
  });
  
  return availableMonths;
};

/**
 * Get the default month to select.
 * Returns current month if available, or the first available unsubmitted month.
 * 
 * @param availableMonths - Array of available month strings
 * @returns The month string to default select, or empty string if none
 */
export const getDefaultMonthSelection = (availableMonths: string[]): string => {
  if (!availableMonths || availableMonths.length === 0) return '';
  
  const currentMonth = getCurrentMonth();
  
  // If current month is available, select it
  if (availableMonths.includes(currentMonth)) {
    return currentMonth;
  }
  
  // Otherwise return the first available month
  return availableMonths[0] || '';
};

// ============================================================
// QUARTER SUBMISSION VALIDATION
// ============================================================

/**
 * Check if submission is allowed for a given quarter.
 * Submission is only allowed on or after the 1st day of the month following the quarter.
 * 
 * Quarter end dates:
 * - Q1 ends March 31 → can submit starting April 1 (exception: Q1 2026 opens March 30 for Easter)
 * - Q2 ends June 30 → can submit starting July 1
 * - Q3 ends September 30 → can submit starting October 1
 * - Q4 ends December 31 → can submit starting January 1 (next year)
 * 
 * @param period - The quarter string (e.g., "Q1 2026")
 * @returns true if submission is allowed, false otherwise
 */
export const isQuarterSubmissionAllowed = (period: string): boolean => {
  if (!period) return false;
  
  // Parse the period string (e.g., "Q1 2026")
  const match = period.match(/Q(\d) (\d{4})/);
  if (!match) return false;
  
  const quarter = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  
  // Special case: Q1 2026 opens early on March 30 due to Easter holidays
  if (quarter === 1 && year === 2026) {
    const submissionAllowedDate = new Date(2026, 2, 30); // March 30, 2026
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= submissionAllowedDate;
  }
  
  // Determine the first day when submission is allowed
  // Q1 ends March 31 → submit allowed starting April 1 (month index 3)
  // Q2 ends June 30 → submit allowed starting July 1 (month index 6)
  // Q3 ends September 30 → submit allowed starting October 1 (month index 9)
  // Q4 ends December 31 → submit allowed starting January 1 next year (month index 0)
  
  let submissionAllowedMonth: number;
  let submissionAllowedYear: number;
  
  if (quarter === 4) {
    // Q4 ends Dec 31, submission allowed from Jan 1 of next year
    submissionAllowedMonth = 0; // January
    submissionAllowedYear = year + 1;
  } else {
    // Q1-Q3: submission allowed from the 1st of the next quarter's first month
    submissionAllowedMonth = quarter * 3; // Q1→3 (April), Q2→6 (July), Q3→9 (October)
    submissionAllowedYear = year;
  }
  
  // Create the submission allowed date (first day of the allowed month)
  const submissionAllowedDate = new Date(submissionAllowedYear, submissionAllowedMonth, 1);
  
  // Get today's date (midnight)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return today >= submissionAllowedDate;
};

/**
 * Get a human-readable message explaining when submission will be allowed.
 * 
 * @param period - The quarter string (e.g., "Q1 2026")
 * @returns Message explaining when submission will be allowed
 */
export const getQuarterSubmissionMessage = (period: string): string => {
  if (!period) return '';
  
  const match = period.match(/Q(\d) (\d{4})/);
  if (!match) return '';
  
  const quarter = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  
  // Special case: Q1 2026 opens early on March 30 due to Easter holidays
  if (quarter === 1 && year === 2026) {
    return `${period} ends on March 31, ${year}. Submission will be allowed starting March 30, ${year} (early opening for Easter holidays).`;
  }
  
  // Quarter end months and submission start dates
  const quarterInfo: Record<number, { endMonth: string; submitMonth: string; submitYear: number }> = {
    1: { endMonth: 'March 31', submitMonth: 'April 1', submitYear: year },
    2: { endMonth: 'June 30', submitMonth: 'July 1', submitYear: year },
    3: { endMonth: 'September 30', submitMonth: 'October 1', submitYear: year },
    4: { endMonth: 'December 31', submitMonth: 'January 1', submitYear: year + 1 },
  };
  
  const info = quarterInfo[quarter];
  if (!info) return '';
  
  return `${period} ends on ${info.endMonth}, ${year}. Submission will be allowed starting ${info.submitMonth}, ${info.submitYear}.`;
};